import React, { useState } from 'react';
import { Box, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import CustomDatePicker from '../../CustomDatePicker';
import SearchableDropdown from '../common/SearchableDropdown';
import { US_STATES } from '../../constants/address';
import ActionButton from '../common/buttons/ActionButton';
import EditProfileButton from '../buttons/EditProfileButton';
import SnackBarAlert from '../common/alerts/SnackBarAlert';
import useSnackBarAlertMessages from '../../hooks/useSnackBarAlertMessages';
import { validatePhoneNumber } from '../../utils/validations';
import validateAddress from '../../utils/validations';
import { useTheme } from '@mui/material/styles';
import { userProfileEditorValidationSchema } from '../../validations/user';
import useBackendApi from '../../hooks/useBackendApi';
import ActionButtonWithLoading from '../common/buttons/ActionButtonWithLoading';

const UserProfileEditor = ({
  newUserAttributes,
  setNewUserAttributes,
  handleUpdateUserAttribute,
  userName,
  setSavedUserAttributes,
  savedUserAttributes,
  handleCloseMessage,
}) => {
  const theme = useTheme();
  const [editMode, setEditMode] = useState(false);
  const [isValidatingInfo, setIsValidatingInfo] = useState(false);
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();
  const { apiFetchRequest } = useBackendApi();

  const initialValues = {
    ...newUserAttributes,
    gender: newUserAttributes.gender || '',
  };

  const onProfileEditorCallback = () => {
    setEditMode(false);
    setIsValidatingInfo(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: userProfileEditorValidationSchema,
    onSubmit: async (values) => {
      setIsValidatingInfo(true);
      const address = {
        address1: values['custom:address_1'],
        address2: values['custom:address_2'],
        city: values['custom:city'],
        state: values['custom:state'],
        zipcode: values['custom:zipcode'],
      };

      const [isPhoneValid, isAddressValid] = await Promise.all([
        validatePhoneNumber(values.phone_number),
        validateAddress(address, apiFetchRequest),
      ]);

      if (!isPhoneValid) {
        formik.setFieldError('phone_number', 'Enter a valid US Phone number');
        formik.setFieldTouched('phone_number', true, false);
      }

      if (isAddressValid !== 'VALID') {
        setAddressErrors();
        addAlertMessages({ message: 'Invalid Address. Please provide the correct address', severityType: 'error' });
      }

      if (isPhoneValid && isAddressValid === 'VALID') {
        formik.setFieldError('phone_number', undefined);
        formik.setFieldError('birthdate', undefined);
        clearAddressErrors();
        try {
          await handleUpdateUserAttribute(
            userName,
            values,
            setSavedUserAttributes,
            setNewUserAttributes,
            onProfileEditorCallback,
          );
        } catch (e) {
          addAlertMessages({ message: 'Failed to update profile. Please try again.', severityType: 'error' });
        } finally {
          setIsValidatingInfo(false);
        }
        return;
      }

      setIsValidatingInfo(false);
    },
  });

  // Helper function to clear address errors
  const clearAddressErrors = () => {
    formik.setFieldError('address_1', undefined);
    formik.setFieldError('address_2', undefined);
    formik.setFieldError('city', undefined);
    formik.setFieldError('state', undefined);
    formik.setFieldError('zipcode', undefined);
  };

  // Helper function to set address errors
  const setAddressErrors = () => {
    formik.setFieldTouched('address_1', true, false);
    formik.setFieldTouched('address_2', true, false);
    formik.setFieldTouched('city', true, false);
    formik.setFieldTouched('state', true, false);
    formik.setFieldTouched('zipcode', true, false);
  };

  const fields = [
    { label: 'First Name', field: 'name', type: 'text', inputType: 'TextField' },
    { label: 'Last Name', field: 'family_name', type: 'text', inputType: 'TextField' },
    { label: 'Gender', field: 'gender', type: 'select', inputType: 'Select' },
    { label: 'Date of Birth', field: 'birthdate', type: 'date', inputType: 'Datepicker' },
    { label: 'Address Line 1', field: 'custom:address_1', type: 'text', inputType: 'TextField' },
    { label: 'Address Line 2', field: 'custom:address_2', type: 'text', inputType: 'TextField' },
    { label: 'City', field: 'custom:city', type: 'text', inputType: 'TextField' },
    { label: 'State', field: 'custom:state', type: 'text', inputType: 'Dropdown' },
    { label: 'ZIP', field: 'custom:zipcode', type: 'text', inputType: 'TextField' },
    { label: 'Mobile', field: 'phone_number', type: 'tel', inputType: 'TextField' },
    { label: 'Email', field: 'email', type: 'email', disabled: true, inputType: 'TextField' }, // Disable email editing
  ];

  const messageFieldDisabled = 'Contact support to update your email';

  const isFieldDisabled = (disabled) => {
    if (!editMode) return true;

    return disabled;
  };

  const renderEditProfileButton = () => {
    if (editMode) return null;

    return <EditProfileButton setEditMode={setEditMode} theme={theme} />;
  };

  const renderActionButtons = () => {
    if (!editMode) return null;

    return (
      <Box sx={{ display: 'flex' }}>
        <ActionButton label="Cancel" onClick={() => setEditMode(false)} />
        <ActionButtonWithLoading label="Save" onClick={formik.handleSubmit} actionLoading={isValidatingInfo} />
      </Box>
    );
  };

  return (
    <Box>
      <SnackBarAlert
        isOpen={savedUserAttributes}
        handleCloseMessage={handleCloseMessage}
        message={'Profile updated successfully'}
      />
      {renderEditProfileButton()}
      <Box sx={{ mb: 2 }}>{renderActionButtons()}</Box>
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            marginBottom: 2,
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            '& .MuiFormControl-root': { width: 'calc(100% - 8px)', m: 1 },
            '& .MuiFormHelperText-root': { ml: 1, mt: 1 },
            [theme.breakpoints.down('sm')]: {
              padding: '0px 23px',
              width: '100%',
              flexWrap: 'nowrap',
              flexDirection: 'column',
              '& .MuiFormControl-root': { width: '100%', m: 0, mb: 1 },
            },
          }}
        >
          {fields.map(({ label, field, type, disabled, inputType }) => (
            <Box
              key={field}
              sx={{
                width: type === 'email' ? '100%' : '50%',
                bgColor: '#ffff',
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                  marginBottom: 1,
                },
              }}
            >
              {inputType === 'TextField' && (
                <TextField
                  label={label}
                  type={type}
                  {...formik.getFieldProps(field)}
                  disabled={isFieldDisabled(disabled)}
                  helperText={
                    formik.touched[field] && formik.errors[field]
                      ? formik.errors[field]
                      : disabled
                        ? messageFieldDisabled
                        : ''
                  }
                  error={formik.touched[field] && Boolean(formik.errors[field])}
                  InputLabelProps={{
                    shrink: Boolean(formik.values[field]),
                  }}
                />
              )}
              {inputType === 'Datepicker' && (
                <CustomDatePicker
                  DatePickerProps={{ disabled: isFieldDisabled(disabled) }}
                  value={formik.values[field] ? dayjs(formik.values[field]) : null}
                  onChange={(newValue) => {
                    if (newValue === null) {
                      formik.setFieldTouched(field, true);
                      formik.setFieldValue(field, '', true);
                      formik.setFieldError(field, null);
                    } else {
                      formik.setFieldTouched(field, true);
                      formik.setFieldValue(field, newValue, true);
                    }
                  }}
                  label={label}
                  error={formik.touched[field] && Boolean(formik.errors[field])}
                  helperText={formik.touched[field] && formik.errors[field] ? formik.errors[field] : ''}
                />
              )}

              {inputType === 'Dropdown' && (
                <SearchableDropdown
                  label={label}
                  options={US_STATES}
                  value={formik.values[field] || ''}
                  onChange={(selectedOption) => formik.setFieldValue(field, selectedOption?.value || '')}
                  disabled={isFieldDisabled(disabled)}
                  helperText={
                    formik.touched[field] && formik.errors[field]
                      ? formik.errors[field]
                      : disabled
                        ? messageFieldDisabled
                        : ''
                  }
                  error={formik.touched[field] && Boolean(formik.errors[field])}
                />
              )}
              {inputType === 'Select' && (
                <FormControl sx={{ m: 1 }} error={formik.touched[field] && Boolean(formik.errors[field])}>
                  <InputLabel id="gender-select-label">{label}</InputLabel>
                  <Select
                    labelId="gender-select-label"
                    id="gender-select"
                    {...formik.getFieldProps(field)}
                    value={formik.values[field] || ''}
                    disabled={isFieldDisabled(disabled)}
                  >
                    <MenuItem value="">
                      <em>Select {label}</em>
                    </MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="unknown">Unknown</MenuItem>
                  </Select>
                  {formik.touched[field] && formik.errors[field] && (
                    <Box sx={{ color: 'red', fontSize: '12px', mt: 1 }}>{formik.errors[field]}</Box>
                  )}
                </FormControl>
              )}
            </Box>
          ))}
        </Box>
      </form>
      {renderSnackBarAlertComponent()}
    </Box>
  );
};

UserProfileEditor.propTypes = {
  newUserAttributes: PropTypes.object.isRequired,
  setNewUserAttributes: PropTypes.func.isRequired,
  handleUpdateUserAttribute: PropTypes.func.isRequired,
  userName: PropTypes.string.isRequired,
  savedUserAttributes: PropTypes.bool.isRequired,
  setSavedUserAttributes: PropTypes.func.isRequired,
  handleCloseMessage: PropTypes.func.isRequired,
};

export default UserProfileEditor;
